import {
  Popper,
  Button,
  Box,
  useTheme,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { createElement, useEffect, useRef, useState } from 'react';
import { CalSickForm } from './types/sick/CalSickForm';
import { CalMeetingForm } from './types/meeting/CalMeetingForm';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import findDifferences from '../findDifferencesInDataCollection';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarSelect from './types/CalendarSelect';
import ExoAlert from '../ExoAlert';
import ExoDialog from '../ExoDialog';
import Cookies from 'universal-cookie';
import { CalMeetingShow } from './types/meeting/CalMeetingShow';
import ExoDrawer from '../ExoDrawer';
import { validateLink } from '../../special/validateLink';
import uuid from 'react-uuid';
import { CalFreeBusyShow } from './types/meeting/CalFreeBusyShow';

export const ExoCalPopper = ({
  anchorEl,
  open,
  onClose,
  data,
  calendars,
  onFeedback,
  onUpdate,
  onDelete,
  editMode,
  setEditMode,
  selectedPeriod,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const config = useConfig();
  const { t } = useTranslation();

  const cookies = new Cookies();
  const userId = cookies.get('userId');
  const selectedCalId = cookies.get('selectedCalId');

  const [type, setType] = useState('meeting');
  const [dataCollection, setDataCollection] = useState({});
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);

  function handleChange(newData) {
    let newDataCollection = { ...dataCollection, ...newData };
    setDataCollection(newDataCollection);
  }

  const typeEditMap = {
    meeting: CalMeetingForm,
    sick: CalSickForm,
  };

  const typeShowMap = {
    meeting: CalMeetingShow,
    freebusy: CalFreeBusyShow,
  };

  useEffect(() => {
    // set default data
    if (data && data.startDateTime && data.endDateTime && open) {
      if (data.id) {
        handleLoadEvent(data.id);
      } else {
        setDataCollection(meetingDefaultData(data, dataCollection));
      }
    }

    // reset type
    setType(data?.type ?? 'meeting');
  }, [data]);

  function handleLoadEvent(eventId) {
    DBRequest({
      config,
      path: `calendar-events/${eventId}`,
      onResponse: handleResponse,
      onLoading: setIsLoading,
    });
  }

  function handleResponse(res) {
    const event = res.data;
    const { userCalendar, startDateTime, endDateTime } = event;
    var userCal = {};
    var firstCalWrite = null;

    if (calendars && calendars.length > 0) {
      userCal = calendars.find((el) => {
        return el.id == userCalendar.id && el.writePermissions == true;
      });
      firstCalWrite = calendars.find((el) => el.writePermissions == true);
    }

    setSelectedCalendar(userCal || firstCalWrite);

    setDataCollection({
      ...event,
      startTime: startDateTime,
      startDate: startDateTime,
      endTime: endDateTime,
      endDate: endDateTime,
    });
  }

  // startData functions
  function meetingDefaultData(event, dataCollection) {
    const { startDateTime, endDateTime, wholeDay = true } = event;

    //In weekly view when creating a new event where wholeDay = false
    const startMoment = moment(startDateTime);
    const endMoment = moment(endDateTime);

    // Get the current time and round it up to the next 30-minute mark
    const now = moment();
    const roundedStart = now
      .clone()
      .add(30 - (now.minute() % 30), 'minutes')
      .startOf('minute');

    // Set the endDateTime to 30 minutes after the rounded start time
    const roundedEnd = roundedStart.clone().add(30, 'minutes');

    //defaultReminder 1 day before for whole day
    const reminders = event.wholeDay
      ? [
          {
            remindAt: moment(startDateTime).subtract(1, 'days'),
            uuid: uuid(),
            type: 'daysBefore',
            value: 1,
          },
        ]
      : [];

    return {
      startTime: wholeDay
        ? roundedStart.toISOString()
        : startMoment.toISOString(),
      startDate: startDateTime,
      endTime: wholeDay ? roundedEnd.toISOString() : endMoment.toISOString(),
      endDate: endDateTime,
      wholeDay: wholeDay ? '1' : '0',
      meetingIsConfirmed: '0',
      label: t('New Meeting'),
      type: 'meeting',
      calendarId: selectedCalId,
      blockTime: true,
      id: event.id,
      owner: {
        id: userId,
      },
      reminders: reminders,
    };
  }

  // calendar change & meeting move logic
  function handleCalendarChange(newCal, isInit) {
    if (
      !isInit &&
      dataCollection.id &&
      dataCollection.calendar?.id !== newCal.id
    )
      handleMoveMeeting(dataCollection, newCal);
    setSelectedCalendar(newCal);
  }

  function handleMoveMeeting(meeting, newCal) {
    DBRequest({
      config,
      path: `calendar-events/${meeting.id}/move`,
      method: API_PATCH({
        fromCalendar: meeting.userCalendar,
        toCalendar: newCal,
      }),
      onFeedback,
      onError: () => setSelectedCalendar(meeting.userCalendar),
      onResponse: (res) =>
        onUpdate({
          ...res.data,
          calendar: res.data.userCalendar,
          color: res.data.userCalendar.color,
          writePermissions: true,
        }),
    });
  }

  function handleSubmit(sendEmails) {
    const startMoment = moment(dataCollection.startTime);
    const endMoment = moment(dataCollection.endTime);

    const startTime = startMoment.format('HH:mm');
    const startDate = moment(dataCollection.startDate).format('YYYY-MM-DD');
    const startMomentMerged = moment(
      `${startDate} ${startTime}`,
      'YYYY-MM-DD HH:mm'
    );

    const endTime = endMoment.format('HH:mm');
    const endDate = moment(dataCollection.endDate).format('YYYY-MM-DD');
    const endMomentMerged = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm');

    const reqData = {
      ...dataCollection,
      startDateTime: startMomentMerged.toISOString(),
      endDateTime: endMomentMerged.toISOString(),
      sendEmails: Boolean(sendEmails),
      calendarId: selectedCalendar.id,
    };

    let dataToSend = findDifferences(data, reqData);
    if (reqData.meetingPartner) {
      dataToSend['meetingPartnerType'] =
        reqData.meetingPartnerType || data.meetingPartnerType;
    }

    dataToSend = {
      ...dataToSend,
      onlineMeeting: dataToSend.onlineMeeting
        ? {
            ...dataToSend.onlineMeeting,
            url: validateLink(dataToSend.onlineMeeting.url),
          }
        : null,
    };

    if (dataToSend.project === undefined) {
      dataToSend.project = null;
    }

    DBRequest({
      config,
      path: dataCollection.id
        ? `calendar-events/${dataCollection.id}`
        : 'calendar-events',
      method: dataCollection.id ? API_PATCH(dataToSend) : API_POST(reqData),
      onFeedback,
      onResponse: (res) => {
        setDataCollection({});
        setOpenAlert(false);
        onUpdate({
          ...res.data,
          calendar: selectedCalendar,
          color: selectedCalendar.color,
        });
        setEditMode(false);
      },
      onError: () => setOpenAlert(false),
    });
    onClose();
    setOpenBlock(false);
  }

  // escape => cancel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleRequest(event);

      if (event.key === 'Delete' && !editMode) handleDelete();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dataCollection]);

  function handleClose(e) {
    if (e) e.preventDefault();
    setDataCollection({});
    setEditMode(false);
    setOpenBlock(false);
    onClose();
  }
  function handleRequest(e) {
    requestSubmit();
  }

  function handleDelete() {
    DBRequest({
      config,
      path: `calendar-events/${dataCollection.id}`,
      method: API_DELETE,
      onFeedback,
      onResponse: () => onDelete(dataCollection.id),
    });
    onClose();
    setOpenAlert(null);
    setEditMode(false);
  }

  const [openAlert, setOpenAlert] = useState(false);
  function requestSubmit() {
    let askMail = false;
    if (type === 'meeting' && dataCollection.participants?.length > 0) {
      askMail = dataCollection.participants.some((p) => p.email);
    }

    if (askMail)
      return setOpenAlert({
        type: 'info',
        message: t('Should an e-mail be sent to the participants?'),
        submitLabel: t('Yes'),
        cancelLabel: t('No'),
        onSubmit: () => handleSubmit(true),
        onCancel: () => handleSubmit(false),
      });
    handleSubmit();
  }

  function requestDelete() {
    if (type === 'meeting' && dataCollection.participants?.length > 0)
      return setOpenAlert({
        type: 'warning',
        message: t(
          'Should the appointment really be deleted? All participants will be notified by e-mail.'
        ),
        submitLabel: t('Yes'),
        cancelLabel: t('No'),
        onSubmit: handleDelete,
        onCancel: () => setOpenAlert(null),
      });
    else {
      handleDelete();
    }
  }

  // mobile view
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [openBlock, setOpenBlock] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const Container = isSmallScreen
    ? editMode || !data?.id
      ? ExoDialog
      : ExoDrawer
    : Popper;

  return (
    <Container
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      placement={selectedPeriod === 'day' ? 'top-start' : 'left-start'}
      className="z-[99] "
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      limitWidth
    >
      <Box
        className="flex flex-row gap-0"
        sx={{
          position: 'relative',
          overflowX: 'hidden',
          transition: 'width 0.3s ease',
          paddingLeft: '5px',
        }}
      >
        <Box
          className="flex flex-col gap-1 pl-3 py-3 rounded-md "
          sx={
            isSmallScreen
              ? {
                  height: '100%',
                }
              : {
                  position: 'relative', // or 'absolute' if needed
                  zIndex: 1,
                  background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
                  border: `1px solid ${colors.selected}`,
                  transform: 'translate(-5px, 0px)',
                  flexShrink: 0,
                  width: openBlock
                    ? '1022px'
                    : editMode || !data?.id
                    ? '450px'
                    : '300px',
                  height: editMode || !data?.id ? '650px' : '350px',
                  maxHeight: '90vh',
                  backdropFilter: `blur(5px)`,
                }
          }
        >
          {!editMode && data?.id ? (
            typeShowMap[type] &&
            createElement(typeShowMap[type], {
              dataCollection,
              onClose: handleClose,
              onEdit: () => setEditMode(true),
              isSmallScreen,
              isLoading,
              wholeDayData: data?.wholeDay,
              writePermissions: data?.writePermissions,
            })
          ) : (
            <>
              <Box className="flex flex-col gap-2 h-full w-full pr-3 overflow-y-auto">
                {data?.id && data?.writePermissions && (
                  <IconButton
                    onClick={requestDelete}
                    className="top-2 right-[50px] z-20 backdrop-blur-sm"
                    sx={{
                      position: 'absolute',
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
                <Typography variant="h5" className="pb-1" fontWeight={500}>
                  {dataCollection.id && data?.writePermissions
                    ? t('Edit Event')
                    : t('New Event')}
                </Typography>
                <Box className="flex flex-col md:flex-row gap-2">
                  <ToggleButtonGroup
                    size="small"
                    value={type}
                    exclusive
                    onChange={(e) => setType(e.target.value)}
                    aria-label="Project Status"
                    className="mb-2 "
                    sx={{
                      '.MuiToggleButton-root': {
                        padding: '2px 5px',
                      },
                      display: 'none',
                    }}
                  >
                    <ToggleButton value="meeting">{t('Meeting')}</ToggleButton>
                    <ToggleButton color="warning" value="sick">
                      {t('Sick')}
                    </ToggleButton>
                    <ToggleButton color="success" value="vacation">
                      {t('Vacation')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Box className="w-full">
                    {type === 'meeting' && (
                      <CalendarSelect
                        calendars={calendars}
                        selectedCal={selectedCalendar}
                        onSelect={handleCalendarChange}
                      />
                    )}
                  </Box>
                </Box>

                {typeEditMap[type] &&
                  createElement(typeEditMap[type], {
                    dataCollection,
                    onChange: handleChange,
                    onValidationChange: setIsValid,
                    openBlock: openBlock,
                    setOpenBlock: setOpenBlock,
                  })}
              </Box>
              <Box className="flex justify-between pt-2 pr-3">
                <Button onClick={handleClose} variant="outlined" color="error">
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={handleRequest}
                  variant="contained"
                  color="success"
                  disabled={!isValid}
                >
                  {t('Submit')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <ExoAlert
        open={Boolean(openAlert)}
        alert={openAlert || {}}
        onClose={openAlert?.onCancel}
        onSubmit={openAlert?.onSubmit}
      />
    </Container>
  );
};
